import { PlusIcon } from "@heroicons/react/20/solid";
import { useState } from "react";
import { Navigate } from "react-router";
import { PageContainer } from "@/layouts/AppLayout";
import { Button, Field, PageSpinner, UserChip } from "@/components/ui";
import { useFeedback } from "@/components/ui/feedback";
import { Modal } from "@/components/ui/Modal";
import { api, errorMessage } from "@/lib/api";
import { keys, useAction, useMe, useUsers } from "@/lib/queries";
import type { User } from "@/lib/types";
import { cn, formatDateTime } from "@/lib/utils";

function generatePassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const bytes = crypto.getRandomValues(new Uint32Array(14));
  return Array.from(bytes, (b) => chars[b % chars.length]).join("");
}

function UserDialog({ user, onClose }: { user: User | null; onClose: () => void }) {
  const me = useMe().data!;
  const { toast } = useFeedback();
  const isNew = !user;
  const [form, setForm] = useState({
    name: user?.name ?? "",
    email: user?.email ?? "",
    password: isNew ? generatePassword() : "",
    isAdmin: user?.isAdmin ?? false,
    isActive: user?.isActive ?? true,
  });
  const [error, setError] = useState("");
  const save = useAction(
    () =>
      isNew
        ? api.createUser({ name: form.name, email: form.email, password: form.password, isAdmin: form.isAdmin })
        : api.updateUser(user.id, { name: form.name, email: form.email, isAdmin: form.isAdmin, isActive: form.isActive, ...(form.password ? { password: form.password } : {}) }),
    {
      invalidate: [keys.users],
      onSuccess: () => {
        if (form.password) {
          navigator.clipboard?.writeText(form.password).catch(() => {});
          toast(isNew ? "User created. Password copied to clipboard — share it securely." : "User updated. New password copied to clipboard.", "success");
        } else toast("User updated", "success");
        onClose();
      },
      onError: (err) => setError(errorMessage(err)),
    },
  );
  const self = user?.id === me.id;

  return (
    <Modal
      open
      onClose={onClose}
      title={isNew ? "New user" : `Edit ${user.name}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()} disabled={!form.name.trim() || !form.email.trim()}>
            {isNew ? "Create user" : "Save"}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <Field label="Full name">
          <input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field" dir="auto" />
        </Field>
        <Field label="Email">
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field" />
        </Field>
        <Field label={isNew ? "Initial password" : "Reset password"} hint={isNew ? "The user can change it from their account settings." : "Leave empty to keep the current password. Resetting signs the user out everywhere."}>
          <div className="flex gap-2">
            <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="field font-mono" placeholder={isNew ? "" : "New password"} />
            <Button onClick={() => setForm({ ...form, password: generatePassword() })}>Generate</Button>
          </div>
        </Field>
        <label className={cn("flex items-start gap-2 text-sm", self && "opacity-50")}>
          <input type="checkbox" checked={form.isAdmin} disabled={self} onChange={(e) => setForm({ ...form, isAdmin: e.target.checked })} className="mt-0.5" />
          <span>
            <span className="font-medium">System administrator</span>
            <span className="block text-muted">Can manage users and access every project as an owner.</span>
          </span>
        </label>
        {!isNew && (
          <label className={cn("flex items-start gap-2 text-sm", self && "opacity-50")}>
            <input type="checkbox" checked={form.isActive} disabled={self} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="mt-0.5" />
            <span>
              <span className="font-medium">Active</span>
              <span className="block text-muted">Deactivated users can't sign in. Their history is kept.</span>
            </span>
          </label>
        )}
        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  );
}

export function AdminUsersPage() {
  const me = useMe().data!;
  const users = useUsers();
  const [editing, setEditing] = useState<User | null | "new">(null);
  const [q, setQ] = useState("");
  if (!me.isAdmin) return <Navigate to="/" replace />;
  const visible = (users.data ?? []).filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <PageContainer>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Users</h1>
          <p className="mt-1 text-sm text-muted">Everyone who can sign in to the workspace. Add people to projects from each project's settings.</p>
        </div>
        <Button variant="primary" icon={PlusIcon} onClick={() => setEditing("new")}>
          New user
        </Button>
      </div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email" className="field mb-4 max-w-72" dir="auto" />
      {users.isPending && <PageSpinner />}
      <div className="overflow-x-auto rounded-lg border border-line">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-start text-[13px] text-muted">
              <th className="px-3 py-2 text-start font-normal">Name</th>
              <th className="px-3 py-2 text-start font-normal">Email</th>
              <th className="px-3 py-2 text-start font-normal">Role</th>
              <th className="px-3 py-2 text-start font-normal">Status</th>
              <th className="px-3 py-2 text-start font-normal">Created</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((u) => (
              <tr key={u.id} onClick={() => setEditing(u)} className="cursor-pointer border-b border-line last:border-0 hover:bg-hover">
                <td className="px-3 py-2">
                  <UserChip id={u.id} name={u.name} size={24} className="font-medium" />
                </td>
                <td className="px-3 py-2 text-muted">{u.email}</td>
                <td className="px-3 py-2">{u.isAdmin ? "Admin" : "User"}</td>
                <td className="px-3 py-2">{u.isActive ? <span className="text-[var(--dot-green)]">Active</span> : <span className="text-faint">Deactivated</span>}</td>
                <td className="px-3 py-2 whitespace-nowrap text-muted">{formatDateTime(u.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <UserDialog user={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </PageContainer>
  );
}
